import React, { FC, useMemo, ReactNode, MouseEvent } from 'react';

import { IconButton } from '@adsk/alloy-react-button';
import {
  DownloadIcon,
  MoreVerticalIcon,
  TrashCanIcon,
} from '@adsk/alloy-react-icon';
import Menu, { MenuItemType } from '@adsk/alloy-react-menu';
import Overlay from '@adsk/alloy-react-overlay';
import theme from '@adsk/alloy-react-theme';

import { ATTACHMENTS_LIST_VARIANTS } from '../../consts';
import { useTranslation } from '../../i18n';
import { AttachmentListVariant } from '../../types';

type AttachmentItemContextualMenuProps = {
  show: boolean;
  onHide: () => void;
  onShow: () => void;
  allowDelete?: boolean;
  onDelete?: () => void;
  allowDownload?: boolean;
  onDownload?: () => void;
  variant?: AttachmentListVariant;
  customItemActions?: MenuItemType<string>[];
  children?: ReactNode;
};

type ExtendedMenuItemType = MenuItemType<string> & {
  show?: boolean;
};

const AttachmentContextualMenu: FC<AttachmentItemContextualMenuProps> = ({
  show,
  onHide,
  onShow,
  allowDownload,
  allowDelete,
  onDelete,
  onDownload,
  variant,
  customItemActions = [],
}) => {
  const { t } = useTranslation();

  const downloadOption = useMemo<ExtendedMenuItemType>(
    () => ({
      show: allowDownload,
      key: 'downloadAttachment',
      label: t('DOWNLOAD'),
      onClick: () => {
        if (onDownload) {
          onDownload();
        }
      },
      renderIcon: () => <DownloadIcon style={{ marginRight: 8 }} />,
    }),
    [t, onDownload, allowDownload],
  );

  const deleteOption = useMemo<ExtendedMenuItemType>(
    () => ({
      show: allowDelete,
      key: 'deleteAttachment',
      label: t('DELETE'),
      onClick: () => {
        if (onDelete) {
          onDelete();
        }
      },
      renderIcon: () => <TrashCanIcon style={{ marginRight: 8 }} />,
    }),
    [t, onDelete, allowDelete],
  );

  const menuOptions = useMemo(
    () =>
      [downloadOption, ...customItemActions, deleteOption]
        .filter((action) => {
          if ('show' in action) {
            return action.show;
          }

          return true;
        })
        .map(({ show, ...action }: ExtendedMenuItemType) => ({
          ...action,
          'data-testid': 'AttachmentItemContextualMenuOption',
          onClick: (e: MouseEvent<HTMLButtonElement>) => {
            // prevent clicking on tile which opens the modal
            e.stopPropagation();

            // execute actual action
            if (action?.onClick) {
              action.onClick(e);
            }

            // hide popup
            onHide();
          },
        })),
    [downloadOption, deleteOption, customItemActions, onHide],
  );

  const targetIconSize = useMemo(
    () => (variant === ATTACHMENTS_LIST_VARIANTS.GRID ? 16 : 24),
    [variant],
  );

  return (
    <Overlay
      show={show}
      onHide={onHide}
      onShow={onShow}
      placement={Overlay.PLACEMENTS.BOTTOM_START}
      renderTarget={({ targetProps, handleToggle }) => (
        <IconButton
          size={targetIconSize}
          {...targetProps}
          onClick={(e) => {
            e.stopPropagation();
            handleToggle();
          }}
          renderIcon={(props) =>
            MoreVerticalIcon.renderIcon({ ...props, size: targetIconSize })
          }
          style={{ color: theme.colors.charcoal900 }}
        />
      )}
      renderOverlay={({
        overlayProps: {
          defaultValue: _ignoredDefaultValue,
          onChange: _ignoredOnChange,
          onClick: _ignoredOnClick,
          ...overlayProps
        },
      }) => (
        <Menu
          {...overlayProps}
          options={menuOptions}
          width={160}
          data-testid="AttachmentItemContextualMenu"
        />
      )}
    />
  );
};

export default AttachmentContextualMenu;
